import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DesignConfiguration } from '@/lib/api/design';
import { designAPI } from '@/lib/api/design';
import {
  createThemeCSSVariables,
  defaultDesignConfiguration,
  parseDesignConfiguration,
} from '@/design-system/theme/configuration-css';
import { DesignContext, type DesignContextType } from '@/contexts/DesignContext';

interface DesignProviderProps {
  children: ReactNode;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (isRecord(error)) {
    const response = error.response;
    if (isRecord(response)) {
      const data = response.data;
      if (isRecord(data) && typeof data.detail === 'string') return data.detail;
    }
    if (typeof error.message === 'string') return error.message;
  }
  return 'Design-Konfiguration konnte nicht geladen werden';
}

async function captureDesignError(message: string): Promise<void> {
  try {
    const sentry = await import('@sentry/react');
    sentry.captureMessage('design.error_load_configuration', {
      level: 'error',
      extra: { message },
    });
  } catch (monitoringError) {
    const normalizedMonitoringError =
      monitoringError instanceof Error ? monitoringError : new Error(String(monitoringError));
    console.error(
      '[monitoring] Sentry konnte den Design-Konfigurationsfehler nicht erfassen',
      normalizedMonitoringError
    );
  }
}

function applyDocumentMetadata(branding: DesignConfiguration['branding']): void {
  document.title = branding.site_title;

  let metaDescription = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (metaDescription === null) {
    metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    document.head.appendChild(metaDescription);
  }
  metaDescription.content = branding.meta_description;

  document.head.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]').forEach((element) => {
    element.remove();
  });

  if (branding.favicon_url) {
    const icon = document.createElement('link');
    icon.rel = 'icon';
    icon.href = branding.favicon_url;
    document.head.appendChild(icon);

    const shortcutIcon = document.createElement('link');
    shortcutIcon.rel = 'shortcut icon';
    shortcutIcon.href = branding.favicon_url;
    document.head.appendChild(shortcutIcon);
    Reflect.set(window, '__APP_FAVICON__', branding.favicon_url);
  }

  const robotsIndex = branding.robots_index === false ? 'noindex' : 'index';
  const robotsFollow = branding.robots_follow === false ? 'nofollow' : 'follow';
  let metaRobots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (metaRobots === null) {
    metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    document.head.appendChild(metaRobots);
  }
  metaRobots.content = `${robotsIndex}, ${robotsFollow}`;
}

export function DesignProvider({ children }: DesignProviderProps): ReactElement {
  const [appliedConfig, setAppliedConfig] = useState<DesignConfiguration | null>(null);
  const [designError, setDesignError] = useState<string | null>(null);

  const {
    data: config,
    error,
    isLoading,
  } = useQuery<DesignConfiguration>({
    queryKey: ['design-configuration'],
    queryFn: async (): Promise<DesignConfiguration> => {
      const response = await designAPI.getDesignConfiguration();
      return parseDesignConfiguration(response.data);
    },
    staleTime: 5 * 60 * 1000,
    retry: 3,
    retryDelay: 1000,
  });

  const applyDesignToDOM = useCallback((designConfig: DesignConfiguration): void => {
    if (!designConfig.enabled) return;
    const root = document.documentElement;
    const cssVariables = createThemeCSSVariables(designConfig);
    for (const [property, value] of Object.entries(cssVariables)) {
      root.style.setProperty(property, value);
    }
    applyDocumentMetadata(designConfig.branding);
    setAppliedConfig(designConfig);
  }, []);

  useEffect((): void => {
    if (config?.enabled) applyDesignToDOM(config);
  }, [applyDesignToDOM, config]);

  useEffect((): void => {
    if (error === null) {
      setDesignError(null);
      return;
    }
    const message = getErrorMessage(error);
    setDesignError(message);
    void captureDesignError(message);
  }, [error]);

  useEffect((): undefined | (() => void) => {
    const isPreview = new URLSearchParams(window.location.search).get('preview') === '1';
    if (!isPreview) return undefined;

    const handler = (event: MessageEvent<unknown>): void => {
      if (event.origin !== window.location.origin || !isRecord(event.data)) return;
      if (event.data.type !== 'design-preview-apply') return;
      const previewConfig = parseDesignConfiguration(event.data.payload);
      applyDesignToDOM(previewConfig);
    };

    window.addEventListener('message', handler);
    return (): void => {
      window.removeEventListener('message', handler);
    };
  }, [applyDesignToDOM]);

  const value = useMemo<DesignContextType>(
    () => ({
      config: appliedConfig ?? config ?? defaultDesignConfiguration,
      isLoading,
      applyDesignToDOM,
      designError,
    }),
    [appliedConfig, applyDesignToDOM, config, designError, isLoading]
  );

  return <DesignContext.Provider value={value}>{children}</DesignContext.Provider>;
}
